<script setup lang="ts">
import { useCdnUrl } from "@/composables/cdn-url";
useMenuState().value = "collapsed";
</script>

<template>
  <NuxtLayout name="text-only">
    <section class="privacy-page-content flex flex-col gap-[3rem]">
      <h1 class="font-bold">Privacy Notice</h1>
      <p><b>Effective Date: December 12, 2024</b></p>
      <article>
        <p>
          This Privacy Notice (“Notice”) applies to personal data Jamestown OTS, L.P. and its
          affiliates, subsidiaries, and related companies that are involved in the operation of One
          Times Square (collectively, “One Times Square,” “OTS,” “we,” “us,” or “our”) collects. One
          Times Square provides space for our tenants (“Tenants”) to provide events and experiences
          for you to attend (each an “Experience”). When you provide personal data for an
          Experience, the data is collected by both OTS and the Tenant offering the Experience. This
          Notice only applies to OTS's collection, use, and sharing of personal data. You should
          visit the Tenant's privacy notice to understand how the Tenant collects, uses, and shares
          personal data.
        </p>
      </article>

      <nav>
        <h2>Table of Contents</h2>
        <ul>
          <li><a href="#dateWeCollect">Data We Collect</a></li>
          <li><a href="#howWeUseData">How We Use Data</a></li>
          <li><a href="#howWeShareData">How We Share Data</a></li>
          <li><a href="#yourChoices">Your Choices</a></li>
          <li><a href="#howWeProtect">How We Protect and Retain Data</a></li>
          <li><a href="#transmission">Transmission Of Data To Other Countries</a></li>
          <li><a href="#thirdParty">Third-Party Applications/Websites</a></li>
          <li><a href="#changes">Changes To This Privacy Notice</a></li>
          <li><a href="#children">Children</a></li>
          <li><a href="#contact">Contact Information</a></li>
          <li>
            <a href="#addtionalInformation">Additional Information For California Residents</a>
          </li>
        </ul>
      </nav>

      <article id="dateWeCollect">
        <h2>Data We Collect</h2>

        <p>We collect data about you in different ways. For example, we collect data:</p>

        <ul>
          <li>
            Directly from you. This includes when you make an account, sign up to get our emails,
            purchase tickets for Experiences or other services, or contact us.
          </li>
          <li>
            Automatically. This includes through cookies, server logs, and other tools on our
            website or apps.
          </li>
          <li>
            From other sources. These can include our affiliates, vendors, social media, publicly
            available sources, and other companies.
          </li>
        </ul>

        <p>The following are a few examples of our collection and use of data:</p>

        <table>
          <thead>
            <tr>
              <th>Context</th>
              <th>Types of Personal Data</th>
              <th>Primary Purpose and Legal Basis</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <th>Your Use of Our Website</th>
              <td>
                We use technology (e.g., a cookie or a pixel) to learn how you engage with our
                websites. This may include which links you click or what you type into our online
                forms. We may also track your IP address, the website that referred you to us, and
                data about your device.
              </td>
              <td>
                We have a legitimate interest in making our website operate. We also use it to
                understand how you interact with our websites, gather analytics, improve our
                websites, and learn your preferences. We may also use this data to help detect and
                prevent fraud. Where required by law, we base the use of technologies upon consent.
              </td>
            </tr>

            <tr>
              <th>Demographics</th>
              <td>We collect personal data, such as your age or your region.</td>
              <td>
                We have a legitimate interest in understanding our users and providing tailored
                services.
              </td>
            </tr>

            <tr>
              <th>Experiences</th>
              <td>
                If you sign up for an Experience you are jointly submitting information to OTS and
                the Tenant operating the experience such as your name, billing, shipping, and email
                addresses, phone number, credit or debit card information, information regarding
                accommodations, and other information necessary to provide you access to the
                Experience.
              </td>
              <td>
                <p>
                  We, or our Tenant, will use your information to perform our contract to provide
                  services to you relating to the Experience.
                </p>

                <p>
                  We also have a legitimate interest in marketing the Experiences provided by other
                  OTS Tenants. If required by law we will seek your permission before sending you
                  marketing communications.
                </p>

                <p>
                  We also have a legitimate interest in understanding analytics regarding
                  Experiences.
                </p>
              </td>
            </tr>

            <tr>
              <th>Job Applicants</th>
              <td>
                If you apply for a job, we collect information needed to process your application.
                If we conduct a background check, we may collect your social security number.
                Providing this information is required for employment.
              </td>
              <td>
                In some contexts, we are required by law to collect data about applicants. We also
                have a legitimate interest in using data to evaluate your application or consider
                you for other positions. If you become an employee, you will be provided with a
                separate privacy notice that explains how we collect, use, and share additional data
                about our employees.
              </td>
            </tr>

            <tr>
              <th>Feedback/Support</th>
              <td>
                If you provide feedback or contact us for support, we collect your name and email,
                as well as other content that you share to reply.
              </td>
              <td>We have a legitimate interest in receiving and acting upon feedback.</td>
            </tr>

            <tr>
              <th>Email List</th>
              <td>When you sign up for our mailing lists, we collect your email address.</td>
              <td>
                We have a legitimate interest in sharing information about our products and
                services. Where required by law we will ask for your consent before communicating
                with you.
              </td>
            </tr>

            <tr>
              <th>Partner Promotion</th>
              <td>
                We collect data that you provide as part of co-branded promotions with other
                companies or Tenants.
              </td>
              <td>We have a legitimate interest in fulfilling our promotions.</td>
            </tr>

            <tr>
              <th>Surveys</th>
              <td>
                We collect data you share through surveys. If a survey is offered by a third-party
                service provider, the third party's privacy notice applies to the collection, use,
                and disclosure of your data.
              </td>
              <td>We have a legitimate interest in understanding your opinions.</td>
            </tr>

            <tr>
              <th>Sweepstakes or contests</th>
              <td>
                When you enter a sweepstakes or contest, we collect data about you such as your
                contact information.
              </td>
              <td>
                We have a legitimate interest in operating the sweepstakes. In some contexts, we are
                also required by law to collect information about those that enter.
              </td>
            </tr>

            <tr>
              <th>Tenant and Vendor Information</th>
              <td>
                We collect the name and contact information of our Tenants and vendors and their
                employees with whom we may interact.
              </td>
              <td>
                We have a legitimate interest in contacting our Tenants and vendors and
                communicating with them about business activities such as projects, services, and
                billing.
              </td>
            </tr>
          </tbody>
        </table>
      </article>

      <article id="howWeUseData">
        <h2>How We Use Data</h2>

        <p>We also use data to:</p>
        <ul>
          <li>Identify you when you visit an Experience, or another location operated by OTS.</li>
          <li>Complete transactions.</li>
          <li>Improve or create services and products.</li>
          <li>Conduct analytics.</li>
          <li>
            Connect with you (e.g., addressing your requests, inquiries, issues, or feedback).
          </li>
          <li>Market our products or services.</li>
          <li>Market the products or services of our Tenants or business partners.</li>
          <li>
            Find and prevent malicious, deceptive, fraudulent, or illegal activity (e.g., activities
            that might harm the rights, property, or safety of our company, users, employees, or
            others).
          </li>
          <li>Find and prevent security incidents.</li>
          <li>Enforce our policies and agreements.</li>
          <li>Debug, find, and fix errors that impair our website and services.</li>
          <li>Comply with legal or regulatory obligations.</li>
          <li>Establish or exercise our rights and defend against legal claims.</li>
          <li>Serve administrative purposes, and manage our relationships.</li>
          <li>For other reasons with your consent.</li>
        </ul>

        <p>
          The sections above describe our main purposes in collecting your data, but often we have
          multiple purposes. For instance, if you purchase tickets to an Experience, we may collect
          your information to perform our contract with you. We also have a legitimate interest in
          maintaining that data so that we can easily address questions about an order. As a result,
          our collection and processing of your data is based in different contexts on your consent,
          our need to perform a contract, our legal obligations, and/or our legitimate interest in
          conducting our business.
        </p>

        <p>
          To the extent we store and use deidentified personal data, we will not try to reidentify
          the information, except to test our deidentification methods.
        </p>
      </article>

      <article id="howWeShareData">
        <h2>How We Share Data</h2>
        <p>
          In addition to the specific situations discussed elsewhere in this Notice we may share
          personal data in the following situations:
        </p>

        <ul>
          <li>
            <b>Tenants.</b> Information collected at an Experience is often collected jointly by the
            Tenant and OTS. When that occurs OTS may share information with a Tenant, and/or the
            Tenant may share information with OTS. We may choose to share experience information
            with other OTS Tenants for the marketing of other existing or future Experiences.
            Tenants' use of your data is not governed by this Notice.
          </li>
          <li>
            <b>Affiliates and Acquisitions.</b> We may share data with our affiliates (e.g., parent
            company, sister companies, subsidiaries, joint ventures, or other companies under common
            control). If a company acquires or enters negotiations to acquire, our company,
            business, or assets, we may share data with that company.
          </li>
          <li>
            <b>Other Disclosures without Your Consent.</b> We may share data to cooperate with law
            enforcement, participate in a legal process, or for legal compliance. We may share your
            data to establish or exercise our rights, to defend against legal claims, to
            investigate, prevent, or act on possible illegal activities, threats to safety of person
            or property, or a violation of our policies. Your data may be shared to ship products to
            or provide services by a third-party agent.
          </li>
          <li>
            <b>Co-Branded Promotions.</b> We may offer contests, sweepstakes, or other promotions
            with third party partners or Tenants. If you enter one that is sponsored by a
            third-party partner or Tenants, the information that you give will be shared with us and
            them. Their use of your data is not governed by this Notice.
          </li>
          <li>
            <b>Service Providers.</b> We may share your data with service providers. Service
            providers may help us to run our website, conduct surveys, provide technical support,
            process payments, fulfill orders, and more.
          </li>
          <li>
            <b>Professional Services.</b> We may share personal data with our professional service
            providers, such as auditors or lawyers.
          </li>
          <li>
            <b>Other Disclosures with Your Consent.</b> We may share your data with third parties
            when you consent or direct us to.
          </li>
        </ul>

        <p>
          Some jurisdictions require us to disclose whether the following categories of personal
          data will be collected, shared with third parties for a “business purpose.” The table
          below indicates the categories of personal data we collect and transfer in a variety of
          contexts. We do not “sell” your personal data for money. As indicated in the chart below
          we may disclose data for the purposes of targeted advertising.
        </p>

        <table>
          <thead>
            <tr>
              <th>Category of Personal Data</th>
              <th>Disclosures for a Business Purpose</th>
              <th>Disclosures for Targeted Advertising</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                <b>Identifiers</b> – this may include real name, alias, postal address, unique
                personal identifier, online identifier, email address, account name, or other
                similar identifiers.
              </td>

              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Business partners</li>
                  <li>Data analytics providers</li>
                  <li>Internet service providers</li>
                  <li>Joint marketing partners</li>
                  <li>Operating systems and platforms</li>
                  <li>Other Service Providers</li>
                  <li>Payment processors and financial institutions</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                  <li>Social networks</li>
                </ul>
              </td>
              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b>Government Issued Identification</b> – this may include social security number,
                driver's license number, or state issued identification number, passport number.
              </td>

              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Other Service Providers</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>
              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b>Financial Information</b> – this may include bank account number, credit card
                number, debit card number, and other financial information.
              </td>
              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Other Service Providers</li>
                  <li>Payment processors and financial institutions</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>
              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b>Commercial information</b> – this may include information about products or
                services purchased, obtained, or considered, or other purchasing or consuming
                histories or tendencies.
              </td>
              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Data analytics providers</li>
                  <li>Joint marketing partners</li>
                  <li>Other Service Providers</li>
                  <li>Payment processors and financial institutions</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>
              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b>Internet or other electronic network activity information</b> – this may include
                browsing history, search history, and information regarding an individual's
                interaction with an internet website, app, or ad.
              </td>

              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Data analytics providers</li>
                  <li>Internet service providers</li>
                  <li>Operating systems and platforms</li>
                  <li>Other Service Providers</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>

              <td>None.</td>
            </tr>

            <tr>
              <td><b>Audio, electronic, visual, thermal, olfactory, or similar information</b></td>
              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Other Service Providers</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>

              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b>Professional or employment-related information</b> – this includes, for example,
                information submitted by job applicants.
              </td>
              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Internet service providers</li>
                  <li>Other Service Providers</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>

              <td>None.</td>
            </tr>

            <tr>
              <td><b>Inferences drawn from any of the information listed above </b></td>
              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Business partners</li>
                  <li>Data analytics providers</li>
                  <li>Joint marketing partners</li>
                  <li>Other Service Providers</li>
                </ul>
              </td>

              <td>None.</td>
            </tr>

            <tr>
              <td>
                <b
                  >Additional categories of personal data described in the California Customer
                  Records statute</b
                ><br />
                (Cal. Civ. Code § 1798.80(e)) – this may include signature, physical
                characteristics, or description, insurance policy number.
              </td>

              <td>
                <ul>
                  <li>Affiliates or subsidiaries</li>
                  <li>Business partners</li>
                  <li>Other Service Providers</li>
                  <li>
                    Professional services organizations, this may include auditors and law firms
                  </li>
                </ul>
              </td>

              <td>None.</td>
            </tr>
          </tbody>
        </table>
      </article>

      <article id="yourChoices">
        <h2>Your Choices</h2>
        <p>
          Some jurisdictions give you a right to make the following choices about your personal
          data:
        </p>

        <ul>
          <li>
            <b>Access.</b> You may request access to your personal data or confirmation that we have
            data about you. In certain limited cases, you may ask to receive access to your data in
            a portable, machine-readable form.
          </li>
          <li>
            <b>List of Third-Party Recipients.</b> You may request a list of specific third parties
            to which we have disclosed personal information. Please note, some jurisdictions also
            allow you to obtain a list of the categories of third parties to which we have disclosed
            personal information. You can find that information in the table above under the “How We
            Share Data” section.
          </li>
          <li>
            <b>Change.</b> Our websites may allow you to change or delete your account profile. If
            our website does not permit you to update or correct certain information, you can reach
            us at the address below to request a change to your information. You may ask us to
            correct inaccurate or incomplete data. Note that we may keep historical data in our
            backup files as permitted by law.
          </li>
          <li>
            <b>Deletion.</b> You may ask us to delete your personal data. If required by law, we
            will grant such a request, but note that in many cases, we must keep your personal data
            to comply with legal obligations, resolve disputes, enforce agreements, or for other
            business purposes.
          </li>
          <li>
            <b>Objection to or Restriction of Certain Processing.</b> In certain circumstances, you
            may object to the processing of your personal data, or ask that we restrict processing
            of your personal data. To do so, follow the instructions below.
          </li>
          <li><b>Online Tracking.</b> We do not recognize the “Do Not Track” signal.</li>
          <li>
            <b>Promotional Emails.</b> You may choose to provide us with your email address to allow
            us to send newsletters, surveys, offers, or other promotional content , as well as
            targeted offers from third parties. You can stop receiving such emails by following the
            unsubscribe instructions at the bottom of those emails. If you choose not to receive
            such emails, we may still send you service-related communications
          </li>

          <li>
            <b>Revocation Of Consent.</b> Where we process your personal data based upon consent,
            you may revoke consent. Note, if you revoke consent for processing personal data, we may
            no longer be able to provide you some types of services.
          </li>
        </ul>

        <p>
          Not all the rights above are absolute, and they do not apply in all circumstances. We may
          limit or deny some requests because the law permits or requires us to. We will not
          discriminate against individuals who exercise their privacy rights under applicable law.
        </p>

        <p>
          <b><i>Submitting Requests</i></b>
        </p>

        <p>
          You may exercise the above rights through our
          <TheNuxtLink
            to="https://privacyportal-cdn.onetrust.com/dsarwebform/4dce15c9-d8c9-4626-a876-32e2fbc2e286/97bade4b-8f81-45ff-9588-6e00f3ce200c.html"
            target="_blank"
            analytics-target="privacy-notice-webform"
            >online portal</TheNuxtLink
          >
          or by contacting us via the contact information below. If you disagree with our denial of
          a request, you may appeal our decision by contacting us with the subject line “Appeal.
        </p>

        <p>
          As required by law, we will require you to prove your identity. We may verify your
          identity by phone call or email. Following a request, we will use reasonable efforts to
          supply, correct or delete personal data about you in our files.
        </p>

        <p>
          In some circumstances, you may designate an authorized agent to exercise certain privacy
          rights on your behalf. If you are an authorized agent submitting a request on behalf of an
          individual, you must attach a copy of a completed
          <TheNuxtLink
            :to="`${useCdnUrl()}/assets/privacy/OTS_Authorized_Agent_Designation_Form.pdf`"
            target="_blank"
            analytics-target="privacy-pdf"
            >Authorized Agent Designation Form</TheNuxtLink
          >
          which shows that you may act on another's behalf.
        </p>
      </article>

      <article id="howWeProtect">
        <h2>How We Protect and Retain Data</h2>

        <p>
          No method of internet transmission or electronic storage is fully secure. While we use
          reasonable efforts to protect your personal data, we cannot guarantee its security. If we
          are required to inform you about a security incident, we will do so electronically, in
          writing, or by phone, as the law permits.
        </p>
        <p>
          Some of our websites permit you to make a password protected account. You are responsible
          for selecting a unique and complex password and keeping it confidential. You are
          responsible for any access to or use of your account by someone who has obtained your
          password, whether or not you approved of such access or use. Notify us of unauthorized use
          of your password or account immediately.
        </p>
        <p>
          We keep your personal data for only as long as necessary to fulfil the purposes in this
          Notice unless a longer retention period is required or permitted by law. This includes the
          purposes of satisfying legal, accounting, or reporting requirements. To determine the
          appropriate retention period for personal data, we consider the amount, nature, and
          sensitivity of the data. We also weigh the potential risk of harm from unauthorized use or
          disclosure of the data, the purposes for which we obtained the data and whether we can
          meet those purposes through other means, as well as applicable legal requirements.
        </p>
      </article>

      <article id="transmission">
        <h2>Transmission Of Data To Other Countries</h2>

        <p>
          Your data may be processed in a country with less stringent privacy laws than the laws in
          the country that you reside. Where possible, we treat personal data using the same privacy
          principles that guide the law of the country in which we first receive your data. By
          submitting your personal data to us you agree to the transfer, storage, and processing of
          your data in a country other than your country of residence including, but not limited to,
          the United States. For more information on our attempts to apply the privacy principles
          applicable in one jurisdiction to data when it goes to another, contact us using the
          contact information below. You may also request a copy of any Standard Contractual Clauses
          we use for the transfer of your data outside of the EEA, which includes the categories of
          information transferred by contacting us using the contact information below.
        </p>
      </article>

      <article id="thirdParty">
        <h2>Third-Party Applications/Websites</h2>

        <p>
          We may provide links to websites and other third-party content or services that we do not
          own or operate. We have no control over the privacy practices of websites or services we
          do not own. For details about such third parties' privacy practices, see their privacy
          notices.
        </p>
      </article>

      <article id="changes">
        <h2>Changes To This Privacy Notice</h2>
        <p>
          We may change our Notice and privacy practices. New notices will be published on our
          website. If changes are material, the Notice that was in place when you submitted personal
          data to us will generally govern that data unless you consent to the new Notice. Our
          Notice shows “effective” and “last updated” dates below. The effective date is the date
          the current version took effect. The last updated date is the date the current version was
          last substantively changed.
        </p>
      </article>

      <article id="children">
        <h2>Children</h2>
        <p>
          Our websites, Experiences, and other services are not meant for children under 13 years
          old, and we do not knowingly collect personal data from children under age 18 without
          parental consent. Children under 13 should not give us their personal data. If a child
          under 13 provides us with personal information without parental or guardian consent, the
          parent or guardian may contact us
          <TheNuxtLink
            to="mailto:Privacy@jamestownlp.com"
            target="_blank"
            analytics-target="email-privacy"
            >Privacy@jamestownlp.com</TheNuxtLink
          >
          and we will remove the information.
        </p>
      </article>

      <article id="contact">
        <h2>Contact Information</h2>
        <p>
          If you have questions, comments, or complaints on our privacy practices, or if you need to
          access this Notice in a different form due to a disability, please contact us. We will try
          to address your requests and provide you with additional privacy-related information.
        </p>
        <ul style="list-style-type: none; margin: 0; padding: 0">
          <li>
            Email:
            <TheNuxtLink
              to="mailto:Privacy@jamestownlp.com"
              target="_blank"
              analytics-target="email-privacy"
              >Privacy@jamestownlp.com</TheNuxtLink
            >
          </li>
          <li>Telephone: +1-888-914-9661 (PIN: 773 067)</li>
          <li>
            Online:
            <TheNuxtLink
              to="https://privacyportal-cdn.onetrust.com/dsarwebform/4dce15c9-d8c9-4626-a876-32e2fbc2e286/97bade4b-8f81-45ff-9588-6e00f3ce200c.html"
              target="_blank"
              analytics-target="privacy-notice-webform"
              >Web Form</TheNuxtLink
            >
          </li>
          <li>Mail: Jamestown, L.P.</li>
          <li>
            675 Ponce de Leon Avenue, 7th Floor<br />
            Atlanta, Georgia 30308
          </li>
        </ul>

        <p>
          If you are not satisfied with our response and are in the European Union or United
          Kingdom, you may have a right to lodge a complaint with your local supervisory authority.
        </p>
      </article>

      <article id="addtionalInformation">
        <h2>Additional Information For California Residents</h2>
        <p>
          If you are a California resident, the following disclosures apply to you in addition to
          the rest of the Notice.
        </p>

        <ul>
          <li>
            <b>California Shine the Light.</b> If you would like more information on the categories
            of personal data (if any) we share with third parties or affiliates for those parties to
            use for direct marketing, submit a written request using the information in the “Contact
            Information” section.
          </li>
          <li>
            <b>California Sensitive Information Disclosure.</b> We do not collect sensitive personal
            data as defined under California law.
          </li>
        </ul>
      </article>
    </section>
  </NuxtLayout>
</template>

<style lang="scss">
.privacy-page-content {
  ul {
    list-style-type: disc;
    margin-left: 4rem;
  }

  article {
    display: flex;
    flex-direction: column;
    gap: 3rem;
    scroll-margin: 90px;
  }

  a {
    color: black !important;
    text-decoration: underline;
  }

  h1 {
    font-size: 4rem;
  }

  h2 {
    font-size: 2.5rem;
  }

  p,
  li,
  th,
  td {
    font-size: 1.75rem;
  }

  table {
    border-spacing: 0;
    th,
    td {
      border: 1px solid black;
      padding: 0.5rem;
      text-align: left;
      vertical-align: top;
      width: 33%;

      ul {
        margin-left: 12px;
        margin-top: 0;
        padding-left: 12px;
        padding-top: 0;
      }

      p {
        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  @media (max-device-width: 405px) {
    li,
    th,
    td,
    p {
      font-size: 1.25rem;
    }
  }
}
</style>
